import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Light from './headers/light';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Footerr from './footers/Footer';
import ScrollToTop from './ScrollToTop';

const CheckoutForm = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('role'); 
    const defaultSupplierId = localStorage.getItem('id');
    const navigate = useNavigate();
    
    const [cart, setCart] = useState([]);
    const [codePromo, setCodePromo] = useState('');
    const [appliedDiscountType, setAppliedDiscountType] = useState(null); // 'loyalty' | 'promo'
    const [discountAmount, setDiscountAmount] = useState(0);
    const [loyaltyPoints, setLoyaltyPoints] = useState(0);
    const [appliedSupplierId, setAppliedSupplierId] = useState(null);
    const [appliedCode, setAppliedCode] = useState('');
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [customer, setCustomer] = useState({
        Name: '',
        Email: '',
        Phone: '',
        Address: '',
        city: '',
        notes: '',
    });

    const userStr = localStorage.getItem("user");
    const userData = userStr ? JSON.parse(userStr) : null;
    const customId = userData ? userData.custom_id : null;

    useEffect(() => {
        const storedCart = JSON.parse(localStorage.getItem('cart')) || [];
        setCart(storedCart);
    }, [token, role, navigate]);

    // Chargement des points de fidélité et pré-remplissage optionnel du code
    useEffect(() => {
        if (customId) {
            axios.get(`https://jolyaparis.cloud/api/loyalty/${customId}`)
                .then(res => {
                    const pointsActifs = res.data.points || res.data.points_actifs || 0;
                    setLoyaltyPoints(pointsActifs);
                    if (role === "client_fidele" && !codePromo) {
                        setCodePromo(customId);
                    }
                })
                .catch(err => console.error("Erreur chargement points:", err));
        }
    }, [customId, role]);

    // --- CALCUL DU SOUS-TOTAL ET REMISES (Exactement comme ProductDetails) ---
    const cartSubtotal = cart.reduce((total, item) => total + item.productPrice * item.quantity, 0);

    useEffect(() => {
        let totalDiscount = 0;

        // 1. Remise automatique de 10% pour les clients fidèles
        if (role === "client_fidele") {
            totalDiscount += cartSubtotal * 0.10;
        }

        // 2. Remise additionnelle par points ou code promo si explicitement appliqués
        if (appliedDiscountType === "loyalty") {
            const pointsDH = loyaltyPoints * 10;
            totalDiscount += pointsDH;
        } else if (appliedDiscountType === "promo") {
            totalDiscount = cartSubtotal * 0.10;
        }

        setDiscountAmount(parseFloat(totalDiscount.toFixed(2)));
    }, [cartSubtotal, appliedDiscountType, loyaltyPoints, role]);

    const shippingFee = (cartSubtotal - discountAmount) >= 300 ? 0 : 20;
    const finalTotal = Math.max(0, Number(((cartSubtotal - discountAmount) + shippingFee).toFixed(2)));

    // --- Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === 'notes' ? value.slice(0, 120) : value;
        setCustomer((prev) => ({ ...prev, [name]: nextValue }));
    };

    const handleQuantityChange = (productID, newQuantity) => {
        if (newQuantity < 1) return;
        const updatedCart = cart.map(item =>
            item.productID === productID ? { ...item, quantity: newQuantity } : item
        );
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    const handleQuantityStep = (productID, delta) => {
        const item = cart.find(i => i.productID === productID);
        if (!item) return;
        handleQuantityChange(productID, item.quantity + delta);
    };

    const handleRemoveItem = (productID) => {
        const updatedCart = cart.filter(item => item.productID !== productID);
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    // APPLICATION DU CODE PROMO OU FIDÉLITÉ (Exactement comme ProductDetails)
    const handlePromoCodeApply = async () => {
        const trimmedCode = codePromo.trim().toUpperCase();
        if (!trimmedCode) {
            Swal.fire("Information", t('Entrer_code_promo') || "Veuillez entrer un code promo.", "info");
            return;
        }

        setIsApplyingPromo(true);

        try {
            const isFideliteCode = trimmedCode.startsWith("CF") || trimmedCode === (customId ? customId.toUpperCase() : "");

            if (isFideliteCode) {
                const loyaltyRes = await axios.get(`https://jolyaparis.cloud/api/loyalty/${trimmedCode}`);
                const pointsActifs = loyaltyRes.data.points || loyaltyRes.data.points_actifs || 0;
                const valeurDH = loyaltyRes.data.valeur_dh || (pointsActifs * 10);

                setLoyaltyPoints(pointsActifs);

                if (pointsActifs <= 0) {
                    Swal.fire("Information", "Ce code de fidélité n'a aucun point actif (0 DH).", "info");
                    setAppliedDiscountType(null);
                    setAppliedCode('');
                    return;
                }

                setAppliedDiscountType("loyalty");
                setAppliedCode(trimmedCode);

                Swal.fire({
                    icon: "success",
                    title: "Code fidélité appliqué !",
                    text: `Remise supplémentaire de -${valeurDH}.00 DH (${pointsActifs} PTS)`
                });
                return;
            }

            const response = await axios.get(`https://jolyaparis.cloud/api/supplier-from-code/${trimmedCode}`);

            if (response.data) {
                setAppliedSupplierId(response.data.supplier_id || response.data?.id);
                setAppliedDiscountType("promo");
                setAppliedCode(trimmedCode);

                Swal.fire({
                    icon: "success",
                    title: "Code promo appliqué !",
                    text: "Remise de -10% sur votre commande"
                });
            } else {
                Swal.fire({ icon: "error", title: t('Code_promo_invalide') || "Code invalide" });
                setAppliedDiscountType(null);
                setAppliedCode('');
            }

        } catch (error) {
            console.error("Erreur promo/loyalty:", error);
            Swal.fire({ icon: "error", title: t('Code_promo_invalide') || "Code invalide ou introuvable" });
            setAppliedDiscountType(null);
            setAppliedCode('');
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!/^0[5-7][0-9]{8}$/.test(customer.Phone)) {
            Swal.fire('Erreur', 'Le numéro de téléphone doit commencer par 0 et contenir 10 chiffres valides du Maroc.', 'error');
            setIsSubmitting(false);
            return;
        }

        const sanitizedNote = (customer.notes || '').trim().slice(0, 120);
        let activeSupplierId = appliedSupplierId || defaultSupplierId;

        if (appliedCode && appliedDiscountType === 'promo' && !appliedSupplierId) {
            try {
                const response = await axios.get(`https://jolyaparis.cloud/api/supplier-from-code/${appliedCode}`);
                activeSupplierId = response.data?.supplier_id || response.data?.id || defaultSupplierId;
            } catch (error) {
                console.error("Erreur récupération supplier ID");
            }
        }

        const customerPayload = {
            ...customer,
            notes: sanitizedNote,
            ...(activeSupplierId && { SupplierID: activeSupplierId }),
        };

        try {
            // 1. Create Customer
            const customerResponse = await axios.post('https://jolyaparis.cloud/api/customers/create', customerPayload, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });

            const customerId = customerResponse.data.customer.CustomerID;

            // 🟢 Logique exacte des points et paiements
            const isPointsApplied = appliedDiscountType === 'loyalty';
            const paymentType = isPointsApplied ? 'point' : 'COD';
            const finalPromoCode = (appliedDiscountType === 'loyalty' || appliedDiscountType === 'promo') ? codePromo.trim() : '';

            // 2. Create Order payload
            const orderData = {
                product_ids: cart.map(item => item.productID),
                prices: cart.map(item => item.productPrice),
                priceGain: cart.map(item => item.priceGain || 0), 
                quantities: cart.map(item => item.quantity),
                customer_id: customerId,
                supplierId: activeSupplierId,
                notes: sanitizedNote,
                total: finalTotal,
                total_price: finalTotal,
                payment_type: paymentType,
                promo_code: finalPromoCode,
                use_points: isPointsApplied,
                discount_amount: discountAmount
            };

            const orderResp = await axios.post('https://jolyaparis.cloud/api/orders/create', orderData, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });

            const { idsOrder, payment_id, supplier_id, message } = orderResp?.data || {};

            sessionStorage.setItem('order_confirmation', JSON.stringify({ idsOrder, payment_id, supplier_id, message }));
            localStorage.removeItem('cart');
            
            setIsSubmitting(false);
            navigate('/order-confirmation', { state: { idsOrder, payment_id, supplier_id, message } });

        } catch (error) {
            console.error("Error details:", error);
            setIsSubmitting(false);
            
            if (error.response) {
                if (error.response.data.error === 'Your account has been deleted.') {
                    Swal.fire('Erreur!', 'Votre compte a été supprimé.', 'error');
                    localStorage.clear();
                    navigate('/signup'); 
                } else {
                    Swal.fire('Erreur!', error.response.data.message || 'Il y a eu un problème, veuillez réessayer.', 'error');
                }
            } else {
                Swal.fire('Erreur!', 'Problème de connexion réseau ou configuration, veuillez réessayer.', 'error');
            }
        }
    };

    return (
        <AnimationRevealPage>
            <ScrollToTop />
            <Light />

            {/* Top banner */}
            <div className="py-4 mb-4" style={{ backgroundColor: '#f5f7fb', width: '100vw', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}>
                <div className="container text-center">
                    <h3 className="mb-1" style={{ color: '#1f2937', fontWeight: 700, fontSize: 24 }}>{t('Shopping_Cart') || 'Shopping Cart'}</h3>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0 justify-content-center">
                            <li className="breadcrumb-item"><a href="/">Home</a></li>
                            <li className="breadcrumb-item active" aria-current="page">{t('Shopping_Cart') || 'Shopping Cart'}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row">
                    {/* Left: Cart Table */}
                    <div className="col-xl-8 mb-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-0">
                                {cart.length > 0 ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table mb-0 align-middle">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th style={{ width: 40 }}></th>
                                                        <th>{t('Produits') || 'Products'}</th>
                                                        <th className="text-end">{t('Prix') || 'Price'}</th>
                                                        <th className="text-center">{t('quantity') || 'Quantity'}</th>
                                                        <th className="text-end">{t('Sous_total') || 'Subtotal'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cart.map((item) => (
                                                        <tr key={item.productID}>
                                                            <td className="text-muted">
                                                                <button onClick={() => handleRemoveItem(item.productID)} className="btn btn-sm btn-link text-muted p-0" title="Remove">×</button>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <img 
                                                                        src={item.productImage?.startsWith('http') ? item.productImage : `https://jolyaparis.cloud/storage/${item.productImage}`} 
                                                                        alt={item.productName || ""} 
                                                                        style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', marginRight: 12 }} 
                                                                    />
                                                                    <div className="fw-semibold text-dark">{item.productName}</div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">{Number(item.productPrice).toFixed(2)} {t('currency')}</td>
                                                            <td>
                                                                <div className="d-flex justify-content-center align-items-center">
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleQuantityStep(item.productID, -1)}>-</button>
                                                                    <input type="text" readOnly className="form-control text-center mx-2" value={item.quantity} style={{ width: 56 }} />
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleQuantityStep(item.productID, 1)}>+</button>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">{(item.productPrice * item.quantity).toFixed(2)} {t('currency')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Coupon Bar & Client Fidèle notice */}
                                        <div className="p-3 border-top bg-light">
                                            {role === "client_fidele" && (
                                                <div className="alert alert-success p-2 small mb-3">
                                                    <i className="bi bi-gift me-2"></i>
                                                    <strong>Statut Client Fidèle :</strong> Remise de 10% appliquée automatiquement sur votre commande !
                                                    <br />
                                                    <strong>Code Fidélité (pour utiliser vos points en sus) :</strong> {customId} ({loyaltyPoints} PTS / {loyaltyPoints * 10} DH)
                                                </div>
                                            )}

                                            <label className="fw-bold mb-2 small">{t('Code_promo', 'Code promo/fidélité')}</label>
                                            <div className="d-flex gap-2 flex-wrap flex-md-nowrap">
                                                <input
                                                    type="text"
                                                    className="form-control p-2"
                                                    placeholder="EX: CFDALAL587 ou REHAB20"
                                                    value={codePromo}
                                                    onChange={(e) => setCodePromo(e.target.value)}
                                                    style={{ borderRadius: '8px', maxWidth: 320 }}
                                                />
                                                <button 
                                                    className="btn text-white px-4 text-nowrap" 
                                                    onClick={handlePromoCodeApply} 
                                                    disabled={isApplyingPromo}
                                                    style={{ backgroundColor: '#4e7662', borderRadius: '8px' }}
                                                >
                                                    {isApplyingPromo ? "..." : t('Appliquer_code_promo', 'Appliquer')}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-4">{t('Panier_vide')}.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Summary Card */}
                    <div className="col-xl-4">
                        <div className="card border-0 shadow-sm mt-5 mt-lg-0">
                            <div className="card-header bg-transparent border-bottom py-3 px-4">
                                <h5 className="font-size-16 mb-0">{t('Resume_commande')}</h5>
                            </div>
                            <div className="card-body p-4 pt-2">
                                <div className="table-responsive">
                                    <table className="table mb-0">
                                        <tbody>
                                            <tr>
                                                <td>{t('Sous_total')}</td>
                                                <td className="text-end">{cartSubtotal.toFixed(2)} {t("currency")}</td>
                                            </tr>

                                            {role === "client_fidele" && (
                                                <tr>
                                                    <td className="text-success small">Remise Client Fidèle (-10%)</td>
                                                    <td className="text-end text-success small">-{(cartSubtotal * 0.10).toFixed(2)} {t("currency")}</td>
                                                </tr>
                                            )}

                                            {appliedDiscountType === "loyalty" && (
                                                <tr>
                                                    <td className="text-success small">Remise Points Fidélité ({loyaltyPoints} PTS)</td>
                                                    <td className="text-end text-success small">-{(loyaltyPoints * 10).toFixed(2)} {t("currency")}</td>
                                                </tr>
                                            )}

                                            {appliedDiscountType === "promo" && (
                                                <tr>
                                                    <td className="text-success small">Remise Code Promo (-10%)</td>
                                                    <td className="text-end text-success small">-{(cartSubtotal * 0.10).toFixed(2)} {t("currency")}</td>
                                                </tr>
                                            )}

                                            <tr>
                                                <td>{t('Frais_livraison')}</td>
                                                <td className="text-end">
                                                    {shippingFee === 0 ? t("Gratuit") : `${shippingFee} ${t("currency")}`}
                                                </td>
                                            </tr>
                                            <tr className="bg-light">
                                                <th>{t('Total')} :</th>
                                                <td className="text-end">
                                                    <span className="fw-bold" style={{ color: "#4e7662" }}>{finalTotal.toFixed(2)} {t("currency")}</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    className="btn w-100 mt-3 text-white fw-bold shadow"
                                    style={{ backgroundColor: '#4e7662', borderRadius: '10px' }}
                                    onClick={() => document.getElementById('customer-form')?.scrollIntoView({ behavior: 'smooth' })}
                                    disabled={cart.length === 0}
                                >
                                    {t('Proceed_To_Checkout') || 'Proceed To Checkout'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Form */}
            <div className="container mt-4" id="customer-form">
                {cart.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <h5 className="mb-3" style={{ color: "#4e7662" }}>{t('Informations_client')}</h5>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <input type="text" name="Name" placeholder={t('Nom')} className="form-control p-3" onChange={handleInputChange} required style={{ borderRadius: '10px' }} />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="Phone" placeholder={t('Numéro_de_Téléphone')} className="form-control p-3" onChange={handleInputChange} required style={{ borderRadius: '10px' }} />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="Address" placeholder={t('Adresse')} className="form-control p-3" onChange={handleInputChange} required style={{ borderRadius: '10px' }} />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="City" placeholder={t('Ville')} className="form-control p-3" onChange={handleInputChange} required style={{ borderRadius: '10px' }} />
                                    </div>
                                    <div className="col-12">
                                        <textarea
                                            name="notes"
                                            placeholder={t('note')}
                                            className="form-control p-3"
                                            rows={3}
                                            maxLength={120}
                                            value={customer.notes}
                                            onChange={handleInputChange}
                                            style={{ borderRadius: '10px' }}
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <button
                                        type="submit"
                                        className="btn w-100 py-3 text-white fw-bold shadow"
                                        disabled={isSubmitting}
                                        style={{
                                            backgroundColor: '#4e7662', border: 'none',
                                            fontSize: '18px', borderRadius: '10px',
                                            cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1
                                        }}
                                    >
                                        {isSubmitting ? `${t('Chargement') || 'Chargement'}...` : t('Confirmer_commande')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
            <Footerr />
        </AnimationRevealPage>
    );
};

export default CheckoutForm;